import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";

import {
  Button,
  ButtonBorder,
  ButtonSize,
  ButtonTheme,
} from "@redotech/redo-web/button";

import { CurrencyContext } from "@redotech/redo-web/currency";
import { memo, useContext } from "react";

import { TeamCustomLayout } from "./custom-package-helper";

import { assertNever } from "@redotech/util/type";
export const CustomLayout = memo(function CustomLayout({
  teamCustomLayout,
  pickupModalMainText,
  pickupModalSubText,
  pickupModalPickupButtonText,
  pickupModalShippingButtonText,
  onChoosePickup,
  onChooseReturnInStore,
  onChooseSelfShip,
  showReturnInStoreOption,
  areAnyOrdersProtected,
  pickupFee,
  nonPickupFee,
  returnInStoreText,
}: {
  teamCustomLayout: TeamCustomLayout;
  pickupModalMainText: string;
  pickupModalSubText: string;
  pickupModalPickupButtonText: string;
  pickupModalShippingButtonText: string;
  onChoosePickup(): void;
  onChooseReturnInStore(): void;
  onChooseSelfShip(): void;
  showReturnInStoreOption: boolean;
  areAnyOrdersProtected: boolean;
  pickupFee: number;
  nonPickupFee: number;
  returnInStoreText: string;
}) {
  switch (teamCustomLayout) {
    case TeamCustomLayout.BALTIC_BORN_ID:
      return (
        <BalticBornLayout
          nonPickupFee={nonPickupFee!}
          onChoosePickup={onChoosePickup}
          onChooseReturnInStore={onChooseReturnInStore}
          onChooseSelfShip={onChooseSelfShip}
          pickupFee={pickupFee}
          pickupModalMainText={pickupModalMainText}
          pickupModalPickupButtonText={pickupModalPickupButtonText}
          pickupModalShippingButtonText={pickupModalShippingButtonText}
          pickupModalSubText={pickupModalSubText}
          returnInStoreText={returnInStoreText}
          showReturnInStoreOption={showReturnInStoreOption}
        />
      );
    case TeamCustomLayout.DONAJO_ID:
      return (
        <DonaJoLayout
          onChoosePickup={onChoosePickup}
          onChooseReturnInStore={onChooseReturnInStore}
          onChooseSelfShip={onChooseSelfShip}
          pickupFee={pickupFee}
          pickupModalMainText={pickupModalMainText}
          pickupModalPickupButtonText={pickupModalPickupButtonText}
          pickupModalShippingButtonText={pickupModalShippingButtonText}
          pickupModalSubText={pickupModalSubText}
          returnInStoreText={returnInStoreText}
          showReturnInStoreOption={showReturnInStoreOption}
        />
      );
    case TeamCustomLayout.BECAUSE_TEES_ID:
    case TeamCustomLayout.INKOPIOUS_ID:
      return (
        <BecauseTeesLayout
          onChoosePickup={onChoosePickup}
          onChooseReturnInStore={onChooseReturnInStore}
          onChooseSelfShip={onChooseSelfShip}
          pickupFee={pickupFee}
          pickupModalMainText={pickupModalMainText}
          pickupModalPickupButtonText={pickupModalPickupButtonText}
          pickupModalShippingButtonText={pickupModalShippingButtonText}
          pickupModalSubText={pickupModalSubText}
          returnInStoreText={returnInStoreText}
          showReturnInStoreOption={showReturnInStoreOption}
        />
      );
    default:
      assertNever(teamCustomLayout);
  }
});

// Subtext not being used, waiting for dynamic content from Maxwell
const BalticBornLayout = memo(function BalticBornLayout({
  pickupModalMainText,
  pickupModalSubText,
  pickupModalPickupButtonText,
  pickupModalShippingButtonText,
  onChoosePickup,
  onChooseReturnInStore,
  onChooseSelfShip,
  showReturnInStoreOption,
  pickupFee,
  nonPickupFee,
  returnInStoreText,
}: {
  pickupModalMainText: string;
  pickupModalSubText: string;
  pickupModalPickupButtonText: string;
  pickupModalShippingButtonText: string;
  onChoosePickup(): void;
  onChooseReturnInStore(): void;
  onChooseSelfShip(): void;
  showReturnInStoreOption: boolean;
  pickupFee: number;
  nonPickupFee: number;
  returnInStoreText: string;
}) {
  const { formatCurrencyFromUSD } = useContext(CurrencyContext);

  return (
    <Flex dir="column" gap="4xl">
      <Flex dir="column" gap="xxs">
        <Text fontSize="lg" fontWeight="semibold">
          {pickupModalMainText}
        </Text>
        <Text fontSize="sm" fontWeight="thin" textColor="tertiary">
          You can schedule a package pickup for{" "}
          {formatCurrencyFromUSD(pickupFee + nonPickupFee)} or ship it back to
          us{" "}
          {nonPickupFee
            ? `for ${formatCurrencyFromUSD(nonPickupFee)}`
            : "at no cost"}
          .
        </Text>
      </Flex>
      <Flex dir="column" gap="lg">
        <Button
          border={ButtonBorder.LIGHT}
          onClick={onChoosePickup}
          size={ButtonSize.LARGE}
          theme={ButtonTheme.OUTLINED}
        >
          {pickupModalPickupButtonText}
        </Button>
        <Flex dir="row" gap="lg" wrap="wrap">
          <Button
            border={ButtonBorder.LIGHT}
            onClick={onChooseSelfShip}
            size={ButtonSize.LARGE}
            style={{ flex: 1 }}
            theme={ButtonTheme.OUTLINED}
          >
            {/* Ship back to us */}
            {pickupModalShippingButtonText}
          </Button>
          {showReturnInStoreOption && (
            <Button
              border={ButtonBorder.LIGHT}
              onClick={onChooseReturnInStore}
              size={ButtonSize.LARGE}
              style={{ flex: 1 }}
              theme={ButtonTheme.OUTLINED}
            >
              {returnInStoreText}
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});

/** @see https://linear.app/redo/issue/RETCO-270/lamour-or-custom-package-pickup-modal */
// Currently L'Amour is switch to the BetaLayout
// Leaving this here for future reference in case we need to switch back
/*
const LAmourLayout = memo(function LAmourLayout({
  pickupModalMainText,
  pickupModalSubText,
  pickupModalPickupButtonText,
  pickupModalShippingButtonText,
  onChoosePickup,
  onChooseReturnInStore,
  onChooseSelfShip,
  showReturnInStoreOption,
  pickupFee,
  returnInStoreText,
}: {
  pickupModalMainText: string;
  pickupModalSubText: string;
  pickupModalPickupButtonText: string;
  pickupModalShippingButtonText: string;
  onChoosePickup(): void;
  onChooseReturnInStore(): void;
  onChooseSelfShip(): void;
  showReturnInStoreOption: boolean;
  pickupFee: number;
  returnInStoreText: string;
}) {
  const { formatCurrencyFromUSD } = useContext(CurrencyContext);

  return (
    <Flex dir="column" gap="4xl">
      <Flex dir="column" gap="xxs">
        <Text fontSize="lg" fontWeight="semibold">
          {pickupModalMainText}
        </Text>
        <Text fontSize="sm" fontWeight="thin" textColor="tertiary">
          {pickupModalSubText.replaceAll(
            "%price%",
            formatCurrencyFromUSD(pickupFee),
          )}
        </Text>
      </Flex>
      <Flex dir="column" gap="lg">
        <Button
          border={ButtonBorder.LIGHT}
          onClick={onChooseSelfShip}
          size={ButtonSize.LARGE}
          theme={ButtonTheme.PRIMARY}
        >
          {pickupModalShippingButtonText}
        </Button>
        <Flex dir="row" gap="lg" wrap="wrap">
          <Button
            border={ButtonBorder.LIGHT}
            onClick={onChoosePickup}
            size={ButtonSize.LARGE}
            style={{ flex: 1 }}
            theme={ButtonTheme.OUTLINED}
          >
            {pickupModalPickupButtonText}
          </Button>
          {showReturnInStoreOption && (
            <Button
              border={ButtonBorder.LIGHT}
              onClick={onChooseReturnInStore}
              size={ButtonSize.LARGE}
              style={{ flex: 1 }}
              theme={ButtonTheme.OUTLINED}
            >
              {returnInStoreText}
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});
*/

/** @see https://linear.app/redo/issue/RETST-17/donajo-or-change-the-package-pickup-modal */
const DonaJoLayout = memo(function DonaJoLayout({
  pickupModalMainText,
  pickupModalSubText,
  pickupModalPickupButtonText,
  pickupModalShippingButtonText,
  onChoosePickup,
  onChooseReturnInStore,
  onChooseSelfShip,
  showReturnInStoreOption,
  pickupFee,
  returnInStoreText,
}: {
  pickupModalMainText: string;
  pickupModalSubText: string;
  pickupModalPickupButtonText: string;
  pickupModalShippingButtonText: string;
  onChoosePickup(): void;
  onChooseReturnInStore(): void;
  onChooseSelfShip(): void;
  showReturnInStoreOption: boolean;
  pickupFee: number;
  returnInStoreText: string;
}) {
  // Use the default layout for now - DonaJo has not specified how they would like it changed
  // https://linear.app/redo/issue/RETST-17/donajo-or-change-the-package-pickup-modal#comment-52baacff
  const { formatCurrencyFromUSD } = useContext(CurrencyContext);

  return (
    <Flex dir="column" gap="4xl">
      <Flex dir="column" gap="xxs">
        <Text fontSize="lg" fontWeight="semibold">
          {pickupModalMainText}
        </Text>
        <Text fontSize="sm" fontWeight="thin" textColor="tertiary">
          {pickupModalSubText.replaceAll(
            "%price%",
            formatCurrencyFromUSD(pickupFee),
          )}
        </Text>
      </Flex>
      <Flex dir="column" gap="lg">
        <Button
          border={ButtonBorder.LIGHT}
          onClick={onChoosePickup}
          size={ButtonSize.LARGE}
          theme={ButtonTheme.PRIMARY}
        >
          {pickupModalPickupButtonText}
        </Button>
        <Flex dir="row" gap="lg" wrap="wrap">
          <Button
            border={ButtonBorder.LIGHT}
            onClick={onChooseSelfShip}
            size={ButtonSize.LARGE}
            style={{ flex: 1 }}
            theme={ButtonTheme.OUTLINED}
          >
            {pickupModalShippingButtonText}
          </Button>
          {showReturnInStoreOption && (
            <Button
              border={ButtonBorder.LIGHT}
              onClick={onChooseReturnInStore}
              size={ButtonSize.LARGE}
              style={{ flex: 1 }}
              theme={ButtonTheme.OUTLINED}
            >
              {returnInStoreText}
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});

/** @see https://linear.app/redo/issue/RETCO-272/because-tees-inkopious-or-package-pickup-text-changes */
const BecauseTeesLayout = memo(function BecauseTeesLayout({
  pickupModalMainText,
  pickupModalSubText,
  pickupModalPickupButtonText,
  pickupModalShippingButtonText,
  onChoosePickup,
  onChooseReturnInStore,
  onChooseSelfShip,
  showReturnInStoreOption,
  pickupFee,
  returnInStoreText,
}: {
  pickupModalMainText: string;
  pickupModalSubText: string;
  pickupModalPickupButtonText: string;
  pickupModalShippingButtonText: string;
  onChoosePickup(): void;
  onChooseReturnInStore(): void;
  onChooseSelfShip(): void;
  showReturnInStoreOption: boolean;
  pickupFee: number;
  returnInStoreText: string;
}) {
  const { formatCurrencyFromUSD } = useContext(CurrencyContext);

  return (
    <Flex dir="column" gap="4xl">
      <Flex dir="column" gap="xxs">
        <Text fontSize="lg" fontWeight="semibold">
          {pickupModalMainText}
        </Text>
        <Text fontSize="sm" fontWeight="thin" textColor="tertiary">
          {pickupModalSubText.replace(
            "%price%",
            formatCurrencyFromUSD(pickupFee),
          )}
        </Text>
      </Flex>
      <Flex dir="column" gap="lg">
        <Button
          border={ButtonBorder.LIGHT}
          onClick={onChoosePickup}
          size={ButtonSize.LARGE}
          theme={ButtonTheme.PRIMARY}
        >
          {pickupModalPickupButtonText.replaceAll(
            "%price%",
            formatCurrencyFromUSD(pickupFee),
          )}
          {/* Schedule package pickup ({formatCurrencyFromUSD(pickupFee)}) */}
        </Button>
        <Flex dir="row" gap="lg" wrap="wrap">
          <Button
            border={ButtonBorder.LIGHT}
            onClick={onChooseSelfShip}
            size={ButtonSize.LARGE}
            style={{ flex: 1 }}
            theme={ButtonTheme.OUTLINED}
          >
            {pickupModalShippingButtonText}
          </Button>
          {showReturnInStoreOption && (
            <Button
              border={ButtonBorder.LIGHT}
              onClick={onChooseReturnInStore}
              size={ButtonSize.LARGE}
              style={{ flex: 1 }}
              theme={ButtonTheme.OUTLINED}
            >
              {returnInStoreText} (free)
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});
